import React, { useRef } from "react";

const useRequest = <IResponse,>(
  getData: (abortSignal: AbortSignal) => Promise<IResponse>,
  onError: (e: any) => void,
) => {
  const [response, setResponse] = React.useState<IResponse | null>(null);
  const [error, setError] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = (showLoading: boolean = true) => {
    // Abort previous request if it’s still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (showLoading) setLoading(true);
    return getData(controller.signal)
      .then((resp) => setResponse(resp))
      .catch((e) => {
        if (e.name === "CanceledError" || e.name === "AbortError") return;

        setResponse(null);
        setError(e);
        onError(e);
      })
      .finally(() => {
        if (showLoading) setLoading(false);
      });
  };

  const resetResponse = () => {
    setResponse(null);
  };

  // Cancels the in-flight request, if any, without starting a new one.
  const cancel = () => {
    abortControllerRef.current?.abort();
  };

  return { response, loading, fetchData, resetResponse, error, cancel };
};

export default useRequest;
